import { Col, Row } from 'reactstrap';
import TableEmptyContent from '../../../components/Text/TableEmptyContent';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { useColumns } from '../hooks/useColumns';
import { TableCollapsable, TablePagination } from '@octano/global-ui';
import {
  ITEMS_PER_PAGE_DEFAULT,
  useTeacherAttendance,
} from './TeacherAttendanceLoader';
import Loading from '../../../components/Info/Loading';
import { useMemo } from 'react';
import DisplayError from '../../../components/Info/DisplayError';
import DownloadReport from './DownloadReport';
import CreateTeacherJustificationForm from './CreateTeacherJustificationForm';
import { convertSubmitToFields } from '../utils';

export default function TeacherAttendanceTable() {
  const { t } = useTranslation(TranslationsKeys.TEACHER_ATTENDANCE_LIST);
  const {
    data,
    loading,
    error,
    refresh,
    query,
    updateQuery,
  } = useTeacherAttendance();

  const columns = useColumns();

  const teacherAttendance = useMemo(() => data?.lessons ?? [], [data]);
  const pagination = useMemo(
    () => ({
      totalItems: data?.pagination.totalItems ?? 0,
      totalPages: data?.pagination.totalPages ?? 0,
    }),
    [data],
  );

  if (loading) {
    return <Loading insideCard />;
  }

  if (error) {
    return (
      <DisplayError
        insideCard
        textBody={error.code}
        retryAction={refresh}
        loadingAction={loading}
      />
    );
  }

  return (
    <Row className="p-2">
      <DownloadReport
        params={{
          sectionId: query.sectionId,
          classStatus: query.classStatus,
        }}
        disableDownload={pagination.totalItems === 0}
      />
      <Col xs={12}>
        <TableCollapsable
          columns={columns}
          isCollapsable={() => true}
          striped={false}
          secondColumn={{
            tdClassName: 'px-4',
            columnName: 'grades',
            headerText: 'teacher-attendance-table-header',
            cellFormat: ({ row }) => {
              return (
                <div style={{ marginRight: '-0.75rem' }}>
                  <CreateTeacherJustificationForm
                    lessonId={row.id}
                    rowJustification={row.hasJustification}
                    defaultValues={convertSubmitToFields(row.id, row)}
                  />
                </div>
              );
            },
          }}
          data={teacherAttendance}
          noResultsText={
            <TableEmptyContent
              title={t(`table.noResults.title`)}
              subtitle={t(`table.noResults.body`)}
            />
          }
        />
        {!!(pagination?.totalPages && pagination?.totalPages > 1) && (
          <TablePagination
            pagination={{
              currentPage: query.page,
              itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
              onChangePage: (page) => {
                updateQuery({ ...query, page });
              },
              totalItems: pagination?.totalItems || 0,
              totalPages: pagination?.totalPages || 0,
            }}
          />
        )}
      </Col>
    </Row>
  );
}
